import { useCallback, useEffect, useRef, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import DOMPurify from "dompurify"

import roadmapRaw from "@/data/roadmap.html?raw"
import { roadmap, type RoadmapDoc } from "@/api/roadmap"
import {
  addBar,
  addRowAfter,
  addVac,
  applyBar,
  applyVac,
  exportSheetHtml,
  injectEditChrome,
  parseBar,
  parseVac,
  removeEditChrome,
  serialize,
  shadowCss,
  type BarProps,
  type VacProps,
} from "@/lib/roadmapDom"
import { RoadmapBarModal, type RoadmapBarState } from "@/components/roadmap/RoadmapBarModal"
import { VacationModal, type VacState } from "@/components/roadmap/VacationModal"
import { LoginDialog } from "@/components/golive/dialogs"
import { golive } from "@/api/golive"
import { launchFireworks, unlockAudio } from "@/lib/fireworks"
import type { ReleaseItem, Releases } from "@/types/sprint-report"

// Fun-only fireworks that erupt from the "System Lockdown" milestone. On by
// default for now (flip FX_DEFAULT to false to hide it later); the URL overrides
// per-visit — `?fireworks=0` hides it, `?fireworks=1` forces it on. When active,
// the 🎆 button shows a per-viewer on/off preference (remembered in localStorage).
const FX_KEY = "roadmap-fireworks"
const FX_DEFAULT = true
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
const FX_AVAILABLE = ((): boolean => {
  try {
    const p = new URLSearchParams(window.location.search).get("fireworks")
    return p === "1" ? true : p === "0" ? false : FX_DEFAULT
  } catch {
    return FX_DEFAULT
  }
})()
const fxEnabled = (): boolean => {
  try {
    return localStorage.getItem(FX_KEY) !== "off"
  } catch {
    return true
  }
}

const SHADOW_CSS = shadowCss(roadmapRaw)
const ORIGINAL_STYLE = /<style>[\s\S]*?<\/style>/.exec(roadmapRaw)?.[0] ?? ""

/** Keep inline styles (grid-column, --pct, --tint) and the classes/data-attrs. */
const clean = (html: string): string => DOMPurify.sanitize(html, { ADD_ATTR: ["style"], ADD_TAGS: ["foot"] })

/** Text-bearing leaves that are inline-editable. Positioned bars are edited via
 *  the modal instead, and the computed actual-delivery row stays read-only. */
const EDITABLE = [
  ".mast h1", ".mast .sub", ".mast .eyebrow", ".mast .facts span",
  ".standing b", ".standing span",
  ".lane",
  ".wkcard .w", ".wkcard .d", ".wkcard .m",
  ".cnt .n", ".cnt .c", ".cnt .kv i", ".cnt .kv em",
  ".bar.rel b", ".bar.rel span", ".bar.relx b", ".bar.relx span",
  ".ms b", ".ms span", ".asat", ".dlvnote",
  ".holbox",
  ".box .t", ".box .v", ".legend span", "foot",
].join(",")

const isModalBar = (el: Element) =>
  el.classList.contains("bar") &&
  !el.classList.contains("ghost") &&
  !el.classList.contains("rel") &&
  !el.classList.contains("relx") &&
  (el.getAttribute("style") ?? "").includes("grid-column")

function fmtWhen(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

// ---- live "Actual delivery breakdown" row (from ADO GA/RC tags) --------------
function buckets(items: ReleaseItem[], tag: string) {
  const tagged = items.filter((i) => i.tags.includes(tag))
  const p1bugs = tagged.filter((i) => i.work_item_type === "Bug" && i.priority?.major === "P1").length
  const nfr = tagged.filter((i) => i.work_item_type === "Project Item").length
  return { total: tagged.length, p1bugs, nfr, other: tagged.length - p1bugs - nfr }
}

function buildActualInner(items: ReleaseItem[], versions: string[]): string {
  const cells = versions
    .slice(0, 10)
    .map((v) => {
      const ga = buckets(items, `GA-${v}`)
      const rc = ga.total === 0
      const b = rc ? buckets(items, `Release-${v}`) : ga
      if (b.total === 0) {
        return `<div class="cnt" style="opacity:.4"><span class="n" style="color:var(--muted);font-weight:400">—</span></div>`
      }
      const cell = rc ? ` style="background:var(--panel-2);border-color:var(--warn);border-style:dashed"` : ""
      const n = rc ? ` style="color:var(--warn)"` : ""
      const mark = rc ? ` <u style="color:var(--warn)">RC</u>` : ""
      return (
        `<div class="cnt"${cell}><span class="n"${n}>${b.total}${mark}</span>` +
        `<span class="kv"><i>Priority Items</i><em>${b.p1bugs}</em></span>` +
        `<span class="kv"><i>Priority NFR</i><em>${b.nfr}</em></span>` +
        `<span class="kv"><i>Other Items</i><em>${b.other}</em></span></div>`
      )
    })
    .join("")
  return `<div class="lane">Actual delivery<br>breakdown</div>${cells}`
}

function fillActualRow(content: HTMLElement, releases: Releases | null | undefined) {
  const anchor = content.querySelector('[data-anchor="actual"]')
  if (!anchor) return
  const versions = [...content.querySelectorAll(".bar.rel b, .bar.relx b")].map((b) => b.textContent?.trim() ?? "")
  anchor.innerHTML = clean(buildActualInner(releases?.items ?? [], versions))
}

/** The 10-Week Roadmap — rendered from the shared, editable backend document in
 *  an isolated Shadow DOM. Version/delivery bars are edited via a modal; text is
 *  edited in place; the "Actual delivery" row is computed live from ADO. */
export function Roadmap({ releases }: { releases: Releases | null | undefined }) {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["roadmap"],
    queryFn: roadmap.get,
    staleTime: 5 * 60 * 1000,
  })

  const hostRef = useRef<HTMLDivElement>(null)
  const shadowRef = useRef<ShadowRoot | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [modal, setModal] = useState<RoadmapBarState | null>(null)
  const [vac, setVac] = useState<VacState | null>(null)

  const [session, setSession] = useState<{ authRequired: boolean; user: string | null }>({
    authRequired: false,
    user: null,
  })
  const [promptOpen, setPromptOpen] = useState(false)
  const [fxOn, setFxOn] = useState(fxEnabled)
  const fxStopRef = useRef<(() => void) | null>(null)
  const fxFiredRef = useRef(false)
  const pendingRef = useRef<(() => void) | null>(null)
  const needsUnlockRef = useRef(false)
  const editingRef = useRef(false)
  const openBarRef = useRef<(el: Element, isNew: boolean) => void>(() => {})
  const openVacRef = useRef<(el: Element, isNew: boolean) => void>(() => {})
  const releasesRef = useRef(releases)

  useEffect(() => {
    golive.whoami().then(setSession).catch(() => {})
  }, [])
  useEffect(() => {
    needsUnlockRef.current = session.authRequired && !session.user
  }, [session])
  useEffect(() => {
    editingRef.current = editing
  }, [editing])
  useEffect(() => {
    releasesRef.current = releases
  }, [releases])

  const withSession = (action: () => void) => {
    if (!needsUnlockRef.current) return action()
    pendingRef.current = action
    setPromptOpen(true)
  }

  const saveNow = useCallback(
    async (html: string) => {
      const updated = await roadmap.save(html)
      queryClient.setQueryData<RoadmapDoc>(["roadmap"], updated)
      return updated
    },
    [queryClient],
  )

  // Attach shadow + delegated listeners once.
  useEffect(() => {
    if (!hostRef.current || shadowRef.current) return
    const shadow = hostRef.current.attachShadow({ mode: "open" })
    const style = document.createElement("style")
    style.textContent = SHADOW_CSS
    const content = document.createElement("div")
    shadow.append(style, content)
    shadowRef.current = shadow
    contentRef.current = content

    content.addEventListener("click", (e) => {
      const t = e.target as HTMLElement
      const c = contentRef.current
      if (!c || !editingRef.current) return
      if (t.closest(".rm-delrow")) {
        t.closest(".row")?.remove()
        return
      }
      if (t.closest(".rm-addrow")) {
        const rows = [...c.querySelectorAll(".timeline > .row")]
        const last = rows[rows.length - 1]
        if (last) {
          addRowAfter(last)
          injectEditChrome(c)
          applyEditable(c, true)
        }
        return
      }
      if (t.closest(".rm-addbar")) {
        const row = t.closest(".row")
        if (!row) return
        // A row that already holds vacation chips gets a new vacation, not a bar.
        if (row.querySelector(".vac")) openVacRef.current(addVac(row), true)
        else openBarRef.current(addBar(row), true)
        return
      }
      const vacEl = t.closest(".vac")
      if (vacEl) {
        openVacRef.current(vacEl, false)
        return
      }
      const bar = t.closest(".bar")
      if (bar && isModalBar(bar)) openBarRef.current(bar, false)
    })
  }, [])

  // One effect owns the shadow content: view repaints + fills the live row; edit
  // layers on the affordances without repainting.
  useEffect(() => {
    const c = contentRef.current
    if (!c || !data) return
    if (editing) {
      applyEditable(c, true)
      injectEditChrome(c)
      return
    }
    applyEditable(c, false)
    c.innerHTML = clean(data.html)
    fillActualRow(c, releases)
  }, [data, editing, releases])

  openBarRef.current = (el, isNew) => setModal({ el, props: parseBar(el), isNew })
  openVacRef.current = (el, isNew) => setVac({ el, props: parseVac(el), isNew })

  const handleEdit = () =>
    withSession(() => {
      setMsg(null)
      setEditing(true)
    })

  const handleCancel = () => {
    setEditing(false)
    setMsg(null)
  }

  const handleSave = async () => {
    const c = contentRef.current
    if (!c) return
    setSaving(true)
    setMsg(null)
    removeEditChrome(c)
    applyEditable(c, false)
    const html = serialize(c, clean)
    try {
      await saveNow(html)
      setEditing(false)
      setMsg("Saved.")
      window.setTimeout(() => setMsg(null), 2500)
    } catch (e) {
      const err = e as Error & { status?: number }
      if (err.status === 401) {
        setSession((s) => ({ ...s, user: null }))
        setPromptOpen(true)
        setMsg("Enter your access code to save.")
      } else {
        setMsg(`Save failed: ${err.message}`)
      }
      applyEditable(c, true)
      injectEditChrome(c)
    } finally {
      setSaving(false)
    }
  }

  const applyBarFromModal = (p: BarProps) => {
    if (modal) applyBar(modal.el, { ...p, html: clean(p.html) })
    setModal(null)
  }
  const deleteBarFromModal = () => {
    modal?.el.remove()
    setModal(null)
  }
  const cancelModal = () => {
    if (modal?.isNew) modal.el.remove()
    setModal(null)
  }

  const applyVacFromModal = (p: VacProps) => {
    if (vac) applyVac(vac.el, p)
    setVac(null)
  }
  const deleteVac = () => {
    vac?.el.remove()
    setVac(null)
  }
  const cancelVac = () => {
    if (vac?.isNew) vac.el.remove()
    setVac(null)
  }

  const downloadPdf = () => {
    const c = contentRef.current
    if (!c) return
    const body = exportSheetHtml(c)
    const iframe = document.createElement("iframe")
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0"
    document.body.appendChild(iframe)
    const idoc = iframe.contentDocument
    if (!idoc) return
    idoc.open()
    idoc.write(`<!doctype html><html data-theme="light"><head><meta charset="utf-8">${ORIGINAL_STYLE}</head><body>${body}</body></html>`)
    idoc.close()
    const go = () => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
      window.setTimeout(() => iframe.remove(), 1000)
    }
    window.setTimeout(go, 120)
  }

  // 🎆 Fire a volley from the "System Lockdown" milestone's on-screen position.
  const fireNow = useCallback(() => {
    fxStopRef.current?.() // don't stack shows
    const c = contentRef.current
    const el = c ? [...c.querySelectorAll(".ms")].find((m) => /System Lockdown/.test(m.textContent ?? "")) : null
    const r = el?.getBoundingClientRect()
    const origin =
      r && r.width
        ? { x: clamp(r.left + r.width / 2, 40, window.innerWidth - 40), y: clamp(r.top + r.height / 2, 40, window.innerHeight - 40) }
        : { x: window.innerWidth / 2, y: window.innerHeight * 0.6 }
    fxStopRef.current = launchFireworks(origin, { sound: true })
  }, [])

  // Auto-show ~2s after the roadmap first paints (once per mount), if enabled.
  useEffect(() => {
    if (!FX_AVAILABLE || !data || editing || !fxOn || fxFiredRef.current) return
    unlockAudio() // best-effort: the tab click that got us here is the gesture
    const t = window.setTimeout(() => {
      fxFiredRef.current = true
      fireNow()
    }, 2000)
    return () => window.clearTimeout(t)
  }, [data, editing, fxOn, fireNow])

  // Stop any running show when leaving the tab.
  useEffect(() => () => fxStopRef.current?.(), [])

  const toggleFx = () => {
    const next = !fxOn
    setFxOn(next)
    try {
      localStorage.setItem(FX_KEY, next ? "on" : "off")
    } catch {
      /* ignore */
    }
    if (next) {
      fxFiredRef.current = true // manual burst counts as this mount's show (no double-fire)
      unlockAudio() // this click is a user gesture → sound is allowed
      fireNow() // instant celebration on enable
    } else {
      fxStopRef.current?.()
      fxStopRef.current = null
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          {data?.updated_at ? (
            <span>
              Last edited {fmtWhen(data.updated_at)}
              {data.updated_by && data.updated_by !== "anonymous" ? ` by ${data.updated_by}` : ""}
            </span>
          ) : (
            <span>Editable roadmap — changes are shared with everyone.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {msg && (
            <span className={/^Saved/.test(msg) ? "text-xs text-emerald-600" : "text-xs text-amber-700"}>{msg}</span>
          )}
          {FX_AVAILABLE && (
            <button
              onClick={toggleFx}
              title={fxOn ? "Fireworks on — click to turn off (just for fun)" : "Fireworks off — click for a burst"}
              aria-pressed={fxOn}
              className={
                "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold shadow-sm transition " +
                (fxOn
                  ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                  : "border-slate-300 text-slate-400 hover:bg-slate-50")
              }
            >
              <span aria-hidden>🎆</span>
              {fxOn ? "Fireworks" : "Fireworks off"}
            </button>
          )}
          <button
            onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            PDF
          </button>
          {!editing ? (
            <button
              onClick={handleEdit}
              className="inline-flex items-center gap-1.5 rounded-lg bg-header px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-ink"
            >
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {editing && (
        <p className="text-xs text-slate-400">
          Click any highlighted text to edit it. Click a bar to change its text, week span, type or
          delete it; click a <b>vacation</b> chip to set its name and dates (auto-placed to the right
          week). <b>+ bar</b> adds a bar (or vacation) to a row, <b>+ row</b> adds a row, <b>×</b>
          deletes a row. The Actual-delivery row is computed from ADO and isn't editable.
        </p>
      )}

      {isLoading && <p className="p-6 text-sm text-slate-500">Loading…</p>}
      {isError && <p className="p-6 text-sm text-red-600">Failed to load: {(error as Error)?.message}</p>}

      <div className="overflow-x-auto rounded-xl border border-slate-200/80 bg-white">
        <div ref={hostRef} />
      </div>

      {modal && (
        <RoadmapBarModal
          state={modal}
          onApply={applyBarFromModal}
          onCancel={cancelModal}
          onDelete={deleteBarFromModal}
        />
      )}

      {vac && <VacationModal state={vac} onApply={applyVacFromModal} onCancel={cancelVac} onDelete={deleteVac} />}

      <LoginDialog
        open={promptOpen}
        onClose={() => {
          pendingRef.current = null
          setPromptOpen(false)
        }}
        onLoggedIn={(name) => {
          setSession({ authRequired: true, user: name })
          setPromptOpen(false)
          const pending = pendingRef.current
          pendingRef.current = null
          pending?.()
        }}
      />
    </div>
  )
}

/** Toggle inline editability; the computed actual-delivery row stays read-only. */
function applyEditable(content: HTMLElement, on: boolean) {
  const root = content.getRootNode()
  const host = root instanceof ShadowRoot ? (root.host as HTMLElement) : null
  if (on) {
    content.querySelectorAll(EDITABLE).forEach((el) => el.setAttribute("contenteditable", "true"))
    content.querySelectorAll('[data-anchor] [contenteditable]').forEach((el) => el.removeAttribute("contenteditable"))
    host?.classList.add("editing")
  } else {
    content.querySelectorAll("[contenteditable]").forEach((el) => el.removeAttribute("contenteditable"))
    host?.classList.remove("editing")
  }
}
